import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Autocomplete, Box, TextField } from '@mui/material';

import { type HotelChainOption, useHotelChainSelector } from './useHotelChainSelector.js';

/** Desktop hotel chain single-select rendered in the AppBar header. Spazious-admin only. */
export function HotelChainSelector() {
  const intl = useIntl();

  const {
    isSpaziousAdmin,
    chainOptions,
    value,
    inputValue,
    setInputValue,
    handleChange,
    handleInputChange,
    handleLoadMore,
    shouldLoadMore,
    isLoading,
  } = useHotelChainSelector();

  const handleListboxScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const listbox = event.currentTarget;
      if (!listbox) return;
      const atBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 8;
      if (atBottom && shouldLoadMore) handleLoadMore();
    },
    [shouldLoadMore, handleLoadMore]
  );

  if (!isSpaziousAdmin) return null;

  const label = intl.formatMessage({
    id: 'HEADER.HOTEL_CHAIN_SELECTOR.LABEL',
    defaultMessage: 'Hotel chain',
  });

  return (
    // The field's only label is an i18n message, so the container carries the test hook.
    <Box data-testid="hotel-chain-selector" sx={{ width: 240 }}>
      <Autocomplete<HotelChainOption, false, boolean, false>
        disableClearable
        fullWidth
        filterOptions={(x) => x}
        getOptionLabel={(o) => o.label || o.value || ''}
        inputValue={inputValue}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        loading={isLoading}
        options={chainOptions}
        renderInput={(params) => <TextField {...params} label={label} size="small" />}
        slotProps={{ listbox: { onScroll: handleListboxScroll } }}
        value={value}
        onChange={(_, val) => {
          // `disableClearable` means MUI never emits null here; the guard only narrows.
          if (val) void handleChange(val);
        }}
        onInputChange={(_, val, reason) => {
          if (reason === 'input') {
            setInputValue(val);
            handleInputChange(val);
          }
          if (reason === 'reset') {
            setInputValue(val);
          }
        }}
      />
    </Box>
  );
}
